import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { IdentityVerificationResponseDto } from '@repo/services/hooks';
import { PortOne, portOneConfig } from '@repo/services/sdk';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useFormik } from 'formik';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { CheckIcon, CheckIconVariant, CloseIcon } from '@repo/ui/Icon';

type Props = {
  identityData?: IdentityVerificationResponseDto;
  postIdentityVerificationTransaction: (identityVerificationId: string) => void;
  closeModal: () => void;
  portOneConfig: portOneConfig;
};

export default function IdentifyVerificationCompleteBody({
  identityData,
  portOneConfig,
  closeModal,
  postIdentityVerificationTransaction,
}: Props) {
  const theme = useTheme();

  const { t } = useTranslation();

  const isVerified = identityData?.status === 'verified';

  const formik = useFormik({
    initialValues: {
      accepts1: isVerified,
      accepts2: isVerified,
      accepts3: isVerified,
      accepts4: isVerified,
    },
    validationSchema: Yup.object({
      accepts1: Yup.boolean().oneOf([true], '모든 항목에 동의해주세요.'),
      accepts2: Yup.boolean().oneOf([true], '모든 항목에 동의해주세요.'),
      accepts3: Yup.boolean().oneOf([true], '모든 항목에 동의해주세요.'),
      accepts4: Yup.boolean().oneOf([true], '모든 항목에 동의해주세요.'),
    }),
    validateOnMount: true,
    onSubmit: () => {},
  });

  const handleIdentityVerification = async () => {
    if (isVerified) return;
    try {
      const response = await PortOne.RequestIdentityVerification(portOneConfig);
      // code가 존재하는 경우
      if (response?.code) {
        return alert(response.message);
      }
      if (response?.identityVerificationId) {
        postIdentityVerificationTransaction(response?.identityVerificationId!);
      }
    } catch (e) {
      return alert(e);
    }
  };

  const handleChangeCheckBox = (e: React.ChangeEvent) => {
    if (isVerified) return;
    formik.handleChange(e);
  };

  const getButtonDesign = () => {
    if (isVerified) return 'filled';
    if (formik.isValid) return 'outline';
    return 'gray';
  };
  const buttonDesign = getButtonDesign();

  return (
    <SWrapper>
      <div className="info-modal-header">
        <h1 className="title">{t('chat.rewards_creator_information_identity_verification')}</h1>
        <button onClick={closeModal} className="close-button">
          <CloseIcon size={24} color={theme.color.icon.icon_4} />
        </button>
      </div>
      <div className="content-box">
        <ul className="list-box" style={{ textAlign: 'left' }}>
          <li className="list-item">
            <p>
              <Trans
                i18nKey="chat.profile_my_edit_verify_modal_personal_information"
                components={{
                  link1: <a className="underline" href={`/policy#privacy`} target="_blank" rel="noreferrer" />,
                }}
              />
            </p>
            <label className={`check-box ${isVerified ? 'cursor-default' : ''}`}>
              <input type="checkbox" name="accepts1" onChange={handleChangeCheckBox} />
              {isVerified ? (
                <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
              ) : (
                <CheckIcon
                  variant={formik.values.accepts1 ? CheckIconVariant.AFFILIATE_ON : CheckIconVariant.AFFILIATE_OFF}
                />
              )}
            </label>
          </li>
          <li className="list-item">
            <p>{t('chat.profile_my_edit_verify_modal_personal_information_transfer')}</p>
            <label className={`check-box ${isVerified ? 'cursor-default' : ''}`}>
              <input type="checkbox" name="accepts2" onChange={handleChangeCheckBox} />
              {isVerified ? (
                <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
              ) : (
                <CheckIcon
                  variant={formik.values.accepts2 ? CheckIconVariant.AFFILIATE_ON : CheckIconVariant.AFFILIATE_OFF}
                />
              )}
            </label>
          </li>
          <li className="list-item">
            <p>{t('chat.profile_my_edit_verify_modal_legally_responsible')}</p>
            <label className={`check-box ${isVerified ? 'cursor-default' : ''}`}>
              <input type="checkbox" name="accepts3" onChange={handleChangeCheckBox} />
              {isVerified ? (
                <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
              ) : (
                <CheckIcon
                  variant={formik.values.accepts3 ? CheckIconVariant.AFFILIATE_ON : CheckIconVariant.AFFILIATE_OFF}
                />
              )}
            </label>
          </li>
          <li className="list-item">
            <p>{t('chat.profile_my_edit_verify_modal_termination_suspension')}</p>
            <label className={`check-box ${isVerified ? 'cursor-default' : ''}`}>
              <input type="checkbox" name="accepts4" onChange={handleChangeCheckBox} />
              {isVerified ? (
                <CheckIcon variant={CheckIconVariant.AFFILIATE_DEFAULT} />
              ) : (
                <CheckIcon
                  variant={formik.values.accepts4 ? CheckIconVariant.AFFILIATE_ON : CheckIconVariant.AFFILIATE_OFF}
                />
              )}
            </label>
          </li>
        </ul>
        <div className="verification-info-container">
          <div className="verification-info-box border-bottom">
            <div className={'placeholder'}>
              {identityData?.name ?? t('identity.verification')}{' '}
              {isVerified && (
                <span style={{ display: 'inline-block' }}>
                  ({t('identity.verification_complete_date')} {/* userLanguage 로 대응 필요 */}
                  {format(new Date(identityData?.verifiedAt), 'yy.MM.dd', {
                    locale: ko,
                  })}
                  )
                </span>
              )}
            </div>
            <button
              className="verification-button"
              onClick={handleIdentityVerification}
              disabled={isVerified || !formik.isValid}
            >
              {t(
                isVerified
                  ? 'chat.rewards_creator_information_completed_button'
                  : 'chat.rewards_creator_information_verify'
              )}
            </button>
          </div>
        </div>
        <div className="caption-box">
          <ul>
            <li>{t('chat.profile_my_edit_verify_modal_one_identify')}</li>
            <li>{t('chat.profile_my_edit_verify_modal_adult_content')}</li>
            <li>{t('chat.profile_my_edit_verify_modal_valid')}</li>
            <li>{t('chat.profile_my_edit_verify_modal_penalties')}</li>
          </ul>
        </div>
      </div>
    </SWrapper>
  );
}

const SWrapper = styled.div`
  padding: 10px 25px 30px;
  width: 100%;
  max-width: 400px;
  background: ${({ theme }) => theme.color.popup.background};
  padding-bottom: 30px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .check-box {
    cursor: pointer;
  }

  .cursor-default {
    cursor: initial;
  }

  .info-modal-header {
    position: relative;
    display: flex;
    height: 60px;
    justify-content: center;
    align-items: center;

    .close-button {
      position: absolute;
      right: 0px;
    }
  }

  .content-box {
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: calc(630px - 72px - 40px); // 72 = 모달 헤더, 40 = 모달 상하 패딩값
    overflow-y: scroll;
    overflow-x: hidden;
    /* Webkit 스크롤바 스타일링 */
    &::-webkit-scrollbar {
      width: 4px;
      height: 28px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 2px;
      background: #ccc;
    }
  }

  .title {
    ${({ theme }) => theme.font.head5_sb};
    color: ${({ theme }) => theme.color.text.default};
  }

  .list-box {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .list-item {
      display: flex;
      gap: 10px;
      justify-content: space-between;

      & > p {
        word-break: break-all;
        ${({ theme }) => theme.font.body2_m};
        color: ${({ theme }) => theme.color.text.TXT_3};
        flex: 1;
      }

      .underline {
        text-decoration: underline;
      }
    }
  }

  .verification-button {
    display: flex;
    height: 36px;
    padding: 6px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    ${({ theme }) => theme.font.body3_m};
    color: ${({ theme }) => theme.color.static.primary};
    border: 1px solid ${({ theme }) => theme.color.static.primary};

    &:disabled {
      border: 1px solid var(--AI-Gray04, #c4c6c9);
      color: var(--Neutral-20, #d8d8d8);
      cursor: not-allowed;
    }
  }

  .verification-info-container {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .verification-info-box {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0px;

      &.border-bottom {
        border-bottom: 1px solid ${({ theme }) => theme.color.popup.line_1};
      }
      &.date {
        font-size: 14px;
      }

      .placeholder {
        color: ${({ theme }) => theme.color.text.TXT_0};
      }
      .name {
        color: ${({ theme }) => theme.color.text.default};
        font-weight: 500;
      }
    }
  }

  .caption-box {
    color: var(--Text-1, #8a8a8a);
    ${({ theme }) => theme.font.caption1_r};

    ul {
      padding: 14px 15px;
      word-break: break-all;
      border-radius: 8px;
      background: ${({ theme }) => theme.color.popup.content};

      li {
        list-style-type: disc;
        list-style-position: inside;
        text-align: left;
        padding-left: 18px;
        text-indent: -12px;
        color: ${({ theme }) => theme.color.text.TXT_1};

        &::marker {
          font-size: 0.7em;
        }
      }
    }
  }
`;
